import { useState, useCallback } from 'react';
import {
  UseKeyboardNavigationReturn,
  KeyboardNavigationConfig,
} from '../../types';

/**
 * Hook for managing keyboard navigation within table cells
 *
 * This hook provides comprehensive keyboard navigation functionality for tables,
 * including arrow key navigation, Enter/Escape handling, and Tab navigation.
 *
 * @param config - Keyboard navigation configuration options
 * @param rowCount - Total number of rows in the table
 * @param columnCount - Total number of columns in the table
 * @param onCellSelect - Optional callback when a cell is selected (Enter key)
 * @param onCellEdit - Optional callback when a cell should enter edit mode
 *
 * @returns Object containing:
 *   - focusedCell: Currently focused cell coordinates (rowIndex, columnIndex)
 *   - setFocusedCell: Function to set the focused cell
 *   - handleKeyDown: Keyboard event handler for navigation
 *
 * @example
 * ```tsx
 * const { focusedCell, setFocusedCell, handleKeyDown } = useKeyboardNavigation(
 *   { enabled: true, navigateOnArrows: true, selectOnEnter: true },
 *   rows.length,
 *   columns.length,
 *   (rowIndex, columnIndex) => console.log(`Selected cell: ${rowIndex}, ${columnIndex}`),
 *   (rowIndex, columnIndex) => console.log(`Edit cell: ${rowIndex}, ${columnIndex}`)
 * );
 *
 * // Set initial focus
 * setFocusedCell(0, 0);
 *
 * // Apply to table container
 * tableElement.onKeyDown = handleKeyDown;
 * tableElement.tabIndex = 0;
 * ```
 */
export const useKeyboardNavigation = (
  config: KeyboardNavigationConfig,
  rowCount: number,
  columnCount: number,
  onCellSelect?: (rowIndex: number, columnIndex: number) => void,
  onCellEdit?: (rowIndex: number, columnIndex: number) => void,
): UseKeyboardNavigationReturn => {
  const [focusedCell, setFocusedCell] = useState<{
    rowIndex: number;
    columnIndex: number;
  } | null>(null);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!config.enabled || !focusedCell) return;

      const { rowIndex, columnIndex } = focusedCell;
      let newRowIndex = rowIndex;
      let newColumnIndex = columnIndex;

      switch (event.key) {
        case 'ArrowUp':
          if (config.navigateOnArrows && rowIndex > 0) {
            newRowIndex = rowIndex - 1;
            event.preventDefault();
          }
          break;
        case 'ArrowDown':
          if (config.navigateOnArrows && rowIndex < rowCount - 1) {
            newRowIndex = rowIndex + 1;
            event.preventDefault();
          }
          break;
        case 'ArrowLeft':
          if (config.navigateOnArrows && columnIndex > 0) {
            newColumnIndex = columnIndex - 1;
            event.preventDefault();
          }
          break;
        case 'ArrowRight':
          if (config.navigateOnArrows && columnIndex < columnCount - 1) {
            newColumnIndex = columnIndex + 1;
            event.preventDefault();
          }
          break;
        case 'Enter':
          if (config.selectOnEnter) {
            onCellSelect?.(rowIndex, columnIndex);
            event.preventDefault();
          }
          if (config.editOnEnter) {
            onCellEdit?.(rowIndex, columnIndex);
            event.preventDefault();
          }
          break;
        case 'Escape':
          if (config.exitEditOnEscape) {
            setFocusedCell(null);
            event.preventDefault();
          }
          break;
        case 'Tab':
          // Move to next cell, wrapping to next row
          if (columnIndex < columnCount - 1) {
            newColumnIndex = columnIndex + 1;
          } else if (rowIndex < rowCount - 1) {
            newRowIndex = rowIndex + 1;
            newColumnIndex = 0;
          }
          event.preventDefault();
          break;
        default:
          return; // Don't update focus for other keys
      }

      if (newRowIndex !== rowIndex || newColumnIndex !== columnIndex) {
        setFocusedCell({ rowIndex: newRowIndex, columnIndex: newColumnIndex });
      }
    },
    [config, focusedCell, rowCount, columnCount, onCellSelect, onCellEdit],
  );

  const updateFocusedCell = useCallback(
    (rowIndex: number, columnIndex: number) => {
      setFocusedCell({ rowIndex, columnIndex });
    },
    [],
  );

  return {
    focusedCell,
    handleKeyDown,
    setFocusedCell: updateFocusedCell,
  };
};
